import type { SessionData } from '../types';
import { AuthKey } from '../crypto/AuthKey';
import { getDC } from '../Utils';
import MemorySession from './Memory';

class StringSession extends MemorySession {
  private _sessionString?: string;
  private _authKeys: Record<number, AuthKey>;

  constructor(sessionString?: string) {
    super();
    this._sessionString = sessionString;
    this._authKeys = {};
  }

  async load() {
    if (!this._sessionString) {
      return;
    }

    try {
      // Parse the session string
      const sessionData = this.parseSessionString(this._sessionString);
      
      if (!sessionData) {
        console.warn('Failed to parse session string');
        return;
      }

      const {
        mainDcId,
        keys,
        isTest,
      } = sessionData;

      const {
        ipAddress,
        port,
      } = getDC(mainDcId);

      this.setDC(mainDcId, ipAddress, port, isTest, true);

      // Load auth keys
      await Promise.all(Object.keys(keys)
        .map(async (dcIdStr) => {
          const dcId = Number(dcIdStr);
          const key = typeof keys[dcId] === 'string'
            ? Buffer.from(keys[dcId] as string, 'hex')
            : Buffer.from(keys[dcId]);

          this._authKeys[dcId] = new AuthKey();
          await this._authKeys[dcId].setKey(key);
        }));

      console.log('✅ StringSession loaded successfully');
    } catch (error) {
      console.error('Failed to load StringSession:', error);
    }
  }

  setDC(dcId: number, serverAddress: string, port: number, isTestServer?: boolean, skipOnUpdate = false) {
    this._dcId = dcId;
    this._serverAddress = serverAddress;
    this._port = port;
    this._isTestServer = isTestServer;

    delete this._authKeys[dcId];

    if (!skipOnUpdate) {
      void this._onUpdate();
    }
  }

  getAuthKey(dcId = this._dcId) {
    return this._authKeys[dcId];
  }

  setAuthKey(authKey: AuthKey, dcId = this._dcId) {
    this._authKeys[dcId] = authKey;
    void this._onUpdate();
  }

  getSessionData(): SessionData {
    const sessionData: SessionData = {
      mainDcId: this._dcId,
      keys: {},
      isTest: this._isTestServer || undefined,
    };

    Object
      .keys(this._authKeys)
      .forEach((dcIdStr) => {
        const dcId = Number(dcIdStr);
        const authKey = this._authKeys[dcId];
        if (!authKey?._key) return;

        sessionData.keys[dcId] = authKey._key.toString('hex');
      });

    return sessionData;
  }

  save(): string {
    const sessionData = this.getSessionData();
    return this.createSessionString(sessionData);
  }

  private parseSessionString(sessionString: string): SessionData | null {
    try {
      // Simple base64 decode and JSON parse approach
      // This is a simplified implementation - in production you might want more robust parsing
      const decoded = atob(sessionString);
      const parsed = JSON.parse(decoded);
      
      return {
        mainDcId: parsed.mainDcId,
        keys: parsed.keys || {},
        isTest: parsed.isTest,
      };
    } catch (error) {
      console.error('Failed to parse session string:', error);
      return null;
    }
  }

  private createSessionString(sessionData: SessionData): string {
    try {
      // Create a JSON string and base64 encode it
      const jsonString = JSON.stringify({
        mainDcId: sessionData.mainDcId,
        keys: sessionData.keys,
        isTest: sessionData.isTest,
        timestamp: Date.now(),
      });
      
      return btoa(jsonString);
    } catch (error) {
      console.error('Failed to create session string:', error);
      return '';
    }
  }

  _onUpdate() {
    // Update the session string when auth keys change
    if (Object.keys(this._authKeys).length > 0) {
      this._sessionString = this.save();
    }
  }

  delete() {
    this._sessionString = undefined;
    this._authKeys = {};
  }
}

export default StringSession;
export { StringSession };